import { useRef, useState } from 'react'

import { getAuthErrorMessage } from '../../services/account/authErrors'
import { errorClass, linkButtonClass } from './styles'

interface DeleteAccountButtonProps {
  onDelete: () => Promise<void>
}

// "Delete account" with an inline confirmation step. On success the account
// (and its session) is gone, so the auth listener returns the Account page to
// the signed-out view and there is nothing to navigate to here.
export function DeleteAccountButton({ onDelete }: DeleteAccountButtonProps) {
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Deletion is irreversible, so guard against a double click with a ref: state is
  // stale within a single tick.
  const inFlight = useRef(false)

  const runDelete = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setError(null)
    setDeleting(true)
    try {
      await onDelete()
    } catch (err) {
      setError(getAuthErrorMessage(err))
      setConfirming(false)
    } finally {
      inFlight.current = false
      setDeleting(false)
    }
  }

  return (
    <div className="mt-4">
      {error && (
        <p role="alert" data-testid="delete-error" className={`${errorClass} mb-2`}>
          {error}
        </p>
      )}

      {confirming ? (
        <div role="alertdialog" aria-label="Delete your account?" className="space-y-3 rounded-lg border border-red-400/30 bg-red-400/5 p-4">
          <p className="text-sm text-white/80">
            This permanently deletes your account and cannot be undone. Game progress saved in this browser is not
            affected.
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              disabled={deleting}
              onClick={() => void runDelete()}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {deleting ? 'Deleting...' : 'Delete account'}
            </button>
            <button type="button" disabled={deleting} onClick={() => setConfirming(false)} className={linkButtonClass}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)} className="text-sm text-red-400 underline hover:text-red-300">
          Delete account
        </button>
      )}
    </div>
  )
}
